import React, { useEffect, useState } from 'react';
import * as submasterService from '../../services/submaster.service';
import CurrencyModal from './CurrencyModal';
import { FaEdit, FaTrash, FaPlus, FaSave, FaStar } from 'react-icons/fa';

const CurrencyManager = ({ type, title }) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await submasterService.getItems(type);
        if (!mounted) return;
        setItems(Array.isArray(data) ? data : data.items || []);
      } catch (e) {
        console.error('Load error', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [type]);

  // Find the default item if one exists
  const defaultItem = items.find(item => item.is_default);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim() || !symbol.trim()) return;
    const payload = { name: name.trim(), symbol: symbol.trim(), active: true };
    try {
      const created = await submasterService.createItem(type, payload);
      const next = created?.data || created || payload;
      setItems((s) => [next, ...s]);
      setName('');
      setSymbol('');
    } catch (e) {
      console.error('Save error', e);
    }
  };

  const handleToggle = async (id, current) => {
    try {
      await submasterService.toggleActive(type, id, !current);
      setItems((s) => s.map((it) => (String(it.id) === String(id) ? { ...it, active: !current } : it)));
    } catch (e) {
      console.error('Toggle error', e);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openEdit = (item) => {
    setEditing(item);
    setModalOpen(true);
  };

  const handleModalSave = async (payload) => {
    try {
      const savePayload = { 
        name: payload.name, 
        symbol: payload.symbol,
        is_default: payload.is_default,
        active: payload.active ?? true
      };

      // If setting this item as default, unset other defaults
      if (payload.is_default && defaultItem && defaultItem.id !== payload.id) {
        await submasterService.updateItem(type, defaultItem.id, { is_default: false });
        setItems((s) => s.map((it) => 
          String(it.id) === String(defaultItem.id) ? { ...it, is_default: false } : it
        ));
      }

      if (payload.id) {
        const updated = await submasterService.updateItem(type, payload.id, savePayload);
        const updatedItem = updated?.data || updated || payload;
        setItems((s) => s.map((it) => (String(it.id) === String(payload.id) ? { ...it, ...updatedItem } : it)));
      } else {
        const created = await submasterService.createItem(type, savePayload);
        const next = created?.data || created || payload;
        setItems((s) => [next, ...s]);
      }
    } catch (e) {
      console.error('Modal save error', e);
    } finally {
      setModalOpen(false);
      setEditing(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this currency?')) return;
    try {
      await submasterService.deleteItem(type, id);
      setItems((s) => s.filter((it) => String(it.id) !== String(id)));
    } catch (e) {
      console.error('Delete error', e);
    }
  };

  return (
    <div>

      <form className="row g-2 align-items-center mb-3" onSubmit={handleSave}>
        <div className="col-auto" style={{ minWidth: 200 }}>
          <input 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            className="form-control" 
            placeholder="Name (e.g., US Dollar)" 
          />
        </div>
        <div className="col-auto" style={{ minWidth: 100 }}>
          <input 
            value={symbol} 
            onChange={(e) => setSymbol(e.target.value)} 
            className="form-control" 
            placeholder="Symbol (e.g., $)" 
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary me-2" type="submit" disabled={!name.trim() || !symbol.trim()}>
            <FaSave className="me-1"/> Save
          </button>
          <button 
            type="button" 
            className="btn btn-outline-success" 
            onClick={() => { setModalOpen(true); setEditing(null); }}
          >
            <FaPlus className="me-1"/> Add
          </button>
        </div>
      </form>

      <CurrencyModal 
        show={modalOpen} 
        onClose={() => setModalOpen(false)} 
        onSave={handleModalSave} 
        initial={editing || { title }} 
        defaultItem={defaultItem}
      />

      <div>
        {loading ? (
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Symbol</th>
                  <th>Default</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5} className="text-center">No currencies found</td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.name}</td>
                    <td><span className="badge bg-info text-dark" style={{ fontSize: '1rem' }}>{it.symbol}</span></td>
                    <td>
                      {it.is_default ? (
                        <span className="badge bg-success">
                          <FaStar className="me-1" /> Default
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{it.active ? 'Yes' : 'No'}</td>
                    <td>
                      <div className="btn-group" role="group">
                        <button 
                          className="btn btn-sm btn-outline-secondary" 
                          onClick={() => handleToggle(it.id, !!it.active)}
                        >
                          {it.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-primary" 
                          onClick={() => openEdit(it)} 
                          title="Edit"
                        >
                          <FaEdit/>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger" 
                          onClick={() => handleDelete(it.id)} 
                          title="Delete"
                        >
                          <FaTrash/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyManager;
